<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import ResizableTextArea from 'shared/components/ResizableTextArea.vue';
import Avatar from 'next/avatar/Avatar.vue';
import FluentIcon from 'shared/components/FluentIcon/Index.vue';

const props = defineProps({
  config: {
    type: Object,
    default: () => ({}),
  },
  appearance: {
    type: Object,
    default: () => ({}),
  },
});

const { t } = useI18n();

const isInputFocused = ref(false);

const getStatusText = computed(() => {
  return props.config.isOnline
    ? t('INBOX_MGMT.WIDGET_BUILDER.BODY.TEAM_AVAILABILITY.ONLINE')
    : t('INBOX_MGMT.WIDGET_BUILDER.BODY.TEAM_AVAILABILITY.OFFLINE');
});

const subtitleFontSize = computed(() => {
  const px = props.appearance?.fontSizePx || 16;
  return `${Math.max(10, Math.round(px * 0.75))}px`;
});
</script>

<template>
  <div
    class="relative flex flex-col w-full px-4"
    :style="{ fontSize: appearance?.fontSize }"
  >
    <div
      v-if="config.isDefaultScreen"
      class="p-4 rounded-md shadow-sm"
      :style="{ backgroundColor: appearance?.cardBackgroundColor }"
    >
      <div class="flex items-center justify-between">
        <div>
          <div
            class="font-medium leading-4"
            :style="{
              fontSize: appearance?.fontSize,
              color: appearance?.textColor,
            }"
          >
            {{ getStatusText }}
          </div>
          <div
            class="mt-1"
            :style="{
              fontSize: subtitleFontSize,
              color: appearance?.mutedTextColor,
            }"
          >
            {{ config.replyTime }}
          </div>
        </div>
        <Avatar name="C" :size="34" rounded-full />
      </div>
      <button
        v-if="config.isDefaultScreen"
        class="inline-flex items-center justify-between px-2 py-1 mt-1 -ml-2 font-medium leading-6 bg-transparent rounded-md text-n-slate-12 dark:bg-transparent"
        :style="{ fontSize: subtitleFontSize, color: config.color }"
      >
        <span class="pr-2">
          {{
            $t(
              'INBOX_MGMT.WIDGET_BUILDER.FOOTER.START_CONVERSATION_BUTTON_TEXT'
            )
          }}
        </span>
        <FluentIcon icon="arrow-right" size="14" />
      </button>
    </div>
    <div
      v-else
      class="flex items-center h-10 rounded-md"
      :class="{ 'ring-2 ring-n-brand': isInputFocused }"
      :style="{ backgroundColor: appearance?.inputBackgroundColor }"
    >
      <ResizableTextArea
        id="chat-input"
        :rows="1"
        :placeholder="
          $t('INBOX_MGMT.WIDGET_BUILDER.FOOTER.CHAT_INPUT_PLACEHOLDER')
        "
        class="flex-grow border-0 outline-none !outline-0 border-none h-8 pb-0 !pt-1.5 resize-none px-3 !mb-0 focus:outline-none rounded-md"
        :style="{
          fontSize: appearance?.fontSize,
          color: appearance?.textColor,
          backgroundColor: appearance?.inputBackgroundColor,
        }"
        @focus="isInputFocused = true"
        @blur="isInputFocused = false"
      />
      <div class="flex items-center gap-2 px-2">
        <FluentIcon icon="emoji" />
        <FluentIcon class="icon-send" icon="send" />
      </div>
    </div>
  </div>
</template>
